<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Link, router } from '@inertiajs/vue3'
import UiInput from '@/Components/UI/UiInput.vue'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import ConfirmDialog from '@/Components/UI/ConfirmDialog.vue'
import ContactAvatar from './ContactAvatar.vue'
import { useInboxStore } from '@/stores/useInboxStore'

const props = defineProps({
  conversation: { type: Object, required: true }
})

const { t } = useI18n()
const store = useInboxStore()

// Polymorphic contact: contact_kind is 'customer' or 'vehicle_source'
// (mirrored from Conversation#contact_type underscore-cased) when linked.
const customer = computed(() => props.conversation.customer)
const supplier = computed(() =>
  props.conversation.contact_kind === 'vehicle_source'
    ? { id: props.conversation.supplier_id, name: props.conversation.supplier_name }
    : null
)
const taken = computed(() => !!props.conversation.taken_at)

// Unlinked state: operator picks which kind to link.
const linkKind = ref('customer')
const search   = ref('')
const results  = ref([])
const showDetach = ref(false)
let debounceTimer = null

const searchUrlFor = (kind, q) =>
  kind === 'supplier'
    ? `/vehicle_sources.json?q=${encodeURIComponent(q)}`
    : `/customers.json?q=${encodeURIComponent(q)}`

watch([search, linkKind], ([q]) => {
  clearTimeout(debounceTimer)
  if (!q || q.length < 2) { results.value = []; return }
  debounceTimer = setTimeout(async () => {
    const res = await fetch(searchUrlFor(linkKind.value, q), {
      headers: { Accept: 'application/json' },
    })
    if (res.ok) results.value = await res.json()
  }, 200)
})

const link = (id) => {
  if (linkKind.value === 'supplier') {
    store.linkSupplier(props.conversation.id, id)
  } else {
    store.linkCustomer(props.conversation.id, id)
  }
}

const detach = () => { showDetach.value = false; store.unlinkCustomer(props.conversation.id) }
const initialsOf = (name) =>
  (name || '?').split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()

const createNewHref = computed(() => {
  const back = encodeURIComponent(`/inbox/conversations/${props.conversation.id}`)
  return linkKind.value === 'supplier'
    ? `/vehicle_sources/new?return_to=${back}`
    : `/customers/new?return_to=${back}&link_conversation=${props.conversation.id}`
})

const promoting = ref(false)
async function promoteToCustomer() {
  if (promoting.value) return
  promoting.value = true
  try {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const res  = await fetch(`/inbox/conversations/${props.conversation.id}/promote_to_customer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf, Accept: 'application/json' }
    })
    const data = await res.json()
    if (res.ok && data.customer_path) {
      router.visit(data.customer_path)
    }
  } finally {
    promoting.value = false
  }
}
</script>

<template>
  <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] p-3">
    <!-- Linked: customer card -->
    <div v-if="customer">
      <div class="flex items-start gap-3">
        <ContactAvatar
          :avatar-url="conversation.contact_avatar"
          :name="customer.full_name"
          :size="36"
          tone="customer"
        />
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-sm fw-value text-[color:var(--ink)] truncate">
              {{ customer.full_name }}
            </span>
            <span
              class="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
              :style="{ background: 'var(--accentSoft)', color: 'var(--accent)' }"
            >
              {{ t('inbox.drawer.kindCustomer') }}
            </span>
          </div>
          <div
            v-if="customer.phone"
            class="text-xs text-[color:var(--muted)] truncate"
          >
            {{ customer.phone }}
          </div>
          <div
            v-if="customer.email"
            class="text-xs text-[color:var(--muted)] truncate"
          >
            {{ customer.email }}
          </div>
        </div>
      </div>
      <div class="mt-2 flex items-center justify-between text-xs">
        <Link
          :href="`/customers/${customer.id}`"
          class="underline text-[color:var(--accent)]"
        >
          {{ t('inbox.drawer.viewProfile') }}
        </Link>
        <button
          v-if="taken"
          type="button"
          class="text-[color:var(--muted)] hover:text-[color:var(--danger)]"
          @click="showDetach = true"
        >
          {{ t('inbox.drawer.detach') }}
        </button>
      </div>
    </div>

    <!-- Linked: supplier card -->
    <div v-else-if="supplier">
      <div class="flex items-start gap-3">
        <ContactAvatar
          :avatar-url="conversation.contact_avatar"
          :name="supplier.name"
          :size="36"
          tone="supplier"
        />
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-sm fw-value text-[color:var(--ink)] truncate">
              {{ supplier.name }}
            </span>
            <span
              class="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
              :style="{ background: 'color-mix(in oklab, var(--warn) 18%, transparent)', color: 'var(--warn)' }"
            >
              {{ t('inbox.drawer.kindSupplier') }}
            </span>
          </div>
        </div>
      </div>
      <div class="mt-2 flex items-center justify-between text-xs">
        <Link
          :href="`/vehicle_sources/${supplier.id}`"
          class="underline text-[color:var(--accent)]"
        >
          {{ t('inbox.drawer.viewSupplier') }}
        </Link>
        <button
          v-if="taken"
          type="button"
          class="text-[color:var(--muted)] hover:text-[color:var(--danger)]"
          @click="showDetach = true"
        >
          {{ t('inbox.drawer.detach') }}
        </button>
      </div>
    </div>

    <!-- Unlinked: kind toggle + search + create-new -->
    <div v-else>
      <p class="text-sm text-[color:var(--muted)] mb-2">
        {{ t('inbox.drawer.unmatched') }}
      </p>
      <p
        v-if="!taken"
        class="text-xs text-[color:var(--muted)]"
      >
        {{ t('inbox.thread.takeFirst') }}
      </p>
      <template v-else>
        <UiSegmented
          v-model="linkKind"
          :options="[
            { value: 'customer', label: t('inbox.drawer.linkAs.customer') },
            { value: 'supplier', label: t('inbox.drawer.linkAs.supplier') }
          ]"
          class="mb-2"
        />
        <UiInput
          v-model="search"
          :placeholder="linkKind === 'supplier' ? t('inbox.drawer.linkSupplier') : t('inbox.drawer.linkCustomer')"
          class="w-full"
        />
        <ul
          v-if="results.length"
          class="mt-2 max-h-40 overflow-y-auto rounded border border-[color:var(--line)]"
        >
          <li
            v-for="r in results"
            :key="r.id"
          >
            <button
              type="button"
              class="w-full text-left px-2 py-1 text-xs hover:bg-[color:var(--surfaceAlt)]"
              @click="link(r.id)"
            >
              <div class="truncate text-[color:var(--ink)]">
                {{ r.full_name || r.name }}
              </div>
              <div class="text-[color:var(--muted)] truncate">
                {{ r.phone || r.email || r.contact_phone || r.contact_email || '' }}
              </div>
            </button>
          </li>
        </ul>
        <Link
          :href="createNewHref"
          class="mt-2 block text-xs underline text-[color:var(--accent)]"
        >
          {{ linkKind === 'supplier' ? t('inbox.drawer.createNewSupplier') : t('inbox.drawer.createNew') }}
        </Link>

        <!-- One-click "promote this Chatwoot contact to a Customer" — pulls
             name + handle + external_id from the conversation payload, opens
             /customers/:id where the operator can fill in email/licence. -->
        <button
          v-if="linkKind === 'customer'"
          type="button"
          class="mt-1 block text-xs text-[color:var(--accent)] underline disabled:opacity-50"
          :disabled="promoting"
          @click="promoteToCustomer"
        >
          {{ promoting ? t('inbox.drawer.promoting') : t('inbox.drawer.promoteToCustomer') }}
        </button>
      </template>
    </div>

    <ConfirmDialog
      :open="showDetach"
      :title="t('inbox.drawer.detach') + '?'"
      consequence=""
      @confirm="detach"
      @cancel="showDetach = false"
    />
  </section>
</template>
